import {
  getApiUrl,
  COMPANY_API,
  getCompanyUserDocumentsEndpoint,
  getCompanyUserDocumentDownloadEndpoint,
  getCompanyUserDocumentDeleteEndpoint,
  getSiteDetailEndpoint,
  getSiteDocumentsEndpoint,
  getSiteDocumentDownloadEndpoint,
  getSiteDocumentDeleteEndpoint
} from './url_helper';
import loadingService from './loading_service';

// Helper function to parse error messages from API responses
const parseErrorMessage = (errorData) => {
  // Check if detail is an array with error objects
  if (errorData.detail && Array.isArray(errorData.detail)) {
    // Extract all "msg" fields and join them
    const messages = errorData.detail
      .map(err => err.msg)
      .filter(msg => msg) // Remove any undefined/null values
      .join(', ');
    return messages || 'An error occurred';
  }
  
  // If detail is a string, return it
  if (errorData.detail && typeof errorData.detail === 'string') {
    return errorData.detail;
  }
  
  // If message exists, return it
  if (errorData.message) {
    return errorData.message;
  }
  
  // Default fallback
  return 'An error occurred';
};

// Common headers for API requests
const getHeaders = (includeAuth = false, skipContentType = false) => {
  const headers = {
    'accept': 'application/json'
  };
  
  if (!skipContentType) {
    headers['Content-Type'] = 'application/json';
  }
  
  if (includeAuth) {
    const token = localStorage.getItem('authToken');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }
  
  return headers;
};

// Generic API call function
const apiCall = async (url, options = {}) => {
  const { showLoading = true, loadingMessage = 'Loading...', ...apiOptions } = options;
  
  try {
    // Show loading if requested
    if (showLoading) {
      loadingService.showLoading(loadingMessage);
    }
    
    // console.log('API call to:', url);
    // console.log('API options:', apiOptions);
    
    // For FormData, don't set Content-Type header (let browser set it with boundary)
    const headers = apiOptions.body instanceof FormData 
      ? { 
          ...getHeaders(apiOptions.includeAuth, true), 
          ...apiOptions.headers 
        }
      : { ...getHeaders(apiOptions.includeAuth), ...apiOptions.headers }; // Use default headers for JSON
    
    // console.log('Final headers:', headers);
    
    const response = await fetch(url, {
      ...apiOptions,
      headers,
    });
    
    // console.log('Response status:', response.status);
    
    const data = await response.json();
    
    if (!response.ok) {
      // Check for 401 error with specific session expired message
      if (response.status === 401 && data.detail && 
          typeof data.detail === 'string' &&
          data.detail.includes("Your session is invalid or has expired")) {
        // Trigger automatic logout
        handleSessionExpired(data.detail);
      }
      throw new Error(parseErrorMessage(data) || `HTTP error! status: ${response.status}`);
    }
    
    return data;
  } catch (error) {
    console.error('API call error:', error);
    throw error;
  } finally {
    // Hide loading if it was shown
    if (showLoading) {
      loadingService.hideLoading();
    }
  }
};

// Same-origin binary download (avoids S3 CORS when fetching files for canvas/PDF)
const apiCallBlob = async (url, options = {}) => {
  const { showLoading = true, loadingMessage = 'Loading...', ...apiOptions } = options;

  try {
    if (showLoading) {
      loadingService.showLoading(loadingMessage);
    }

    const headers =
      apiOptions.body instanceof FormData
        ? { ...getHeaders(apiOptions.includeAuth, true), ...apiOptions.headers }
        : { ...getHeaders(apiOptions.includeAuth, true), ...apiOptions.headers };

    const response = await fetch(url, {
      ...apiOptions,
      headers,
    });

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const data = await response.json();
        if (response.status === 401 && data.detail &&
            typeof data.detail === 'string' &&
            data.detail.includes('Your session is invalid or has expired')) {
          handleSessionExpired(data.detail);
        }
        errorMessage = parseErrorMessage(data) || errorMessage;
      } catch {
        // Response body is not JSON.
      }
      throw new Error(errorMessage);
    }

    return response.blob();
  } catch (error) {
    console.error('API blob call error:', error);
    throw error;
  } finally {
    if (showLoading) {
      loadingService.hideLoading();
    }
  }
};

// Handle session expired - automatic logout
const handleSessionExpired = (message) => {
  console.log('Session expired detected:', message);
  
  // Import session manager and OAuth logout functions
  import('./session_manager').then(({ default: sessionManager }) => {
    import('./oauth_logout').then(({ handleOAuthLogout, detectOAuthProvider, getOAuthAccessToken, clearAllOAuthData }) => {
      // Clear session and local storage
      sessionManager.clearSession();
      localStorage.removeItem('userData');
      localStorage.removeItem('userFullName');
      localStorage.removeItem('userProfilePic');
      clearAllOAuthData();
      
      // Handle OAuth logout if applicable
      const oauthProvider = detectOAuthProvider();
      if (oauthProvider) {
        const accessToken = getOAuthAccessToken(oauthProvider);
        handleOAuthLogout(oauthProvider, accessToken).catch(console.error);
      }
      
      // Redirect to login page with message
      const loginUrl = new URL('/login', window.location.origin);
      loginUrl.searchParams.set('message', encodeURIComponent(message));
      loginUrl.searchParams.set('type', 'error');
      window.location.href = loginUrl.toString();
    });
  });
};

// Authentication API calls
export const authAPI = {
  // Login
  login: async (credentials) => {
    return apiCall(getApiUrl(COMPANY_API.LOGIN), {
      method: 'POST',
      body: JSON.stringify(credentials),
      loadingMessage: 'Signing you in...'
    });
  },

  // Register
  register: async (formData) => {
    return apiCall(getApiUrl(COMPANY_API.REGISTER), {
      method: 'POST',
      body: formData, // FormData for file uploads
      loadingMessage: 'Creating your account...'
    });
  },

  // Verify OTP
  verifyOTP: async (otpData) => {
    return apiCall(getApiUrl(COMPANY_API.VERIFY_OTP), {
      method: 'POST',
      body: JSON.stringify(otpData),
      loadingMessage: 'Verifying OTP...'
    });
  },

  // Resend OTP
  resendOTP: async (emailData) => {
    return apiCall(getApiUrl(COMPANY_API.RESEND_OTP), {
      method: 'POST',
      body: JSON.stringify(emailData),
      loadingMessage: 'Resending OTP...'
    });
  },

  // Forgot Password
  forgotPassword: async (emailOrUsernameData) => {
    return apiCall(getApiUrl(COMPANY_API.FORGOT_PASSWORD), {
      method: 'POST',
      body: JSON.stringify({ email_or_username: emailOrUsernameData }),
    });
  },

  // Verify OTP for Forgot Password
  verifyOtpForgotPassword: async ({ email_or_username, otp_code }) => {
    return apiCall(getApiUrl(COMPANY_API.VERIFY_OTP_FORGOT_PASSWORD), {
      method: 'POST',
      body: JSON.stringify({ email_or_username, otp_code }),
    });
  },

  // Reset Password
  resetPassword: async ({ new_password, reset_token }) => {
    return apiCall(getApiUrl(COMPANY_API.RESET_PASSWORD), {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${reset_token}` },
      body: JSON.stringify({ new_password }),
    });
  },

  // Logout
  logout: async () => {
    return apiCall(getApiUrl(COMPANY_API.LOGOUT), {
      method: 'POST',
      includeAuth: true,
    });
  },

  // Social Login (Google, etc.)
  socialLogin: async (socialData) => {
    return apiCall(getApiUrl(COMPANY_API.SOCIAL_LOGIN), {
      method: 'POST',
      body: JSON.stringify(socialData),
    });
  },

  // Expire Last Device
  expireLastDevice: async () => {
    return apiCall(getApiUrl(COMPANY_API.EXPIRE_LAST_DEVICE), {
      method: 'POST',
      includeAuth: true,
      body: JSON.stringify({}),
    });
  },

  // Expire Specific Device
  expireDevice: async (sessionId) => {
    return apiCall(getApiUrl(COMPANY_API.EXPIRE_DEVICE), {
      method: 'POST',
      includeAuth: true,
      body: JSON.stringify({ session_id: sessionId }),
    });
  },
};

// Data API calls
export const dataAPI = {
  // Get regions and industry list
  getRegionIndustryList: async () => {
    return apiCall(getApiUrl(COMPANY_API.REGION_INDUSTRY_LIST), {
      method: 'GET',
    });
  },

  // Complete profile
  completeProfile: async (profileData) => {
    return apiCall(getApiUrl(COMPANY_API.COMPLETE_PROFILE), {
      method: 'POST',
      includeAuth: true,
      body: JSON.stringify(profileData),
      loadingMessage: 'Completing your profile...'
    });
  },

  // Edit company profile
  editCompany: async (companyData) => {
    return apiCall(getApiUrl(COMPANY_API.EDIT_COMPANY), {
      method: 'PUT',
      includeAuth: true,
      body: companyData, // FormData for file upload
      loadingMessage: 'Updating company profile...'
    });
  },

  // Edit user profile
  editUserProfile: async (userData) => {
    return apiCall(getApiUrl(COMPANY_API.EDIT_USER_PROFILE), {
      method: 'PUT',
      includeAuth: true,
      body: userData, // FormData for file upload
      loadingMessage: 'Updating user profile...'
    });
  },

  // Upload document
  uploadDocument: async (documentData, options = {}) => {
    return apiCall(getApiUrl(COMPANY_API.UPLOAD_DOCUMENT), {
      method: 'POST',
      includeAuth: true,
      body: documentData, // FormData for file upload
      loadingMessage: 'Uploading document...',
      ...options
    });
  },

  // Upload multiple documents
  uploadMultipleDocuments: async (documentData, options = {}) => {
    return apiCall(getApiUrl(COMPANY_API.UPLOAD_MULTIPLE_DOCUMENTS), {
      method: 'POST',
      includeAuth: true,
      body: documentData, // FormData for multiple file upload
      loadingMessage: 'Uploading documents...',
      ...options
    });
  },

  // Get all documents
  getAllDocuments: async (showLoading = false) => {
    return apiCall(getApiUrl(COMPANY_API.GET_ALL_DOCUMENTS), {
      method: 'GET',
      includeAuth: true,
      showLoading,
      loadingMessage: 'Loading documents...'
    });
  },

  // Employee-specific documents
  getEmployeeDocuments: async (userId, options = {}) => {
    return apiCall(getApiUrl(getCompanyUserDocumentsEndpoint(userId)), {
      method: 'GET',
      includeAuth: true,
      loadingMessage: 'Loading documents...',
      ...options
    });
  },

  uploadEmployeeDocuments: async (userId, documentData, options = {}) => {
    return apiCall(getApiUrl(getCompanyUserDocumentsEndpoint(userId)), {
      method: 'POST',
      includeAuth: true,
      body: documentData,
      loadingMessage: 'Uploading documents...',
      ...options
    });
  },

  downloadEmployeeDocument: async (userId, documentId, options = {}) => {
    return apiCall(getApiUrl(getCompanyUserDocumentDownloadEndpoint(userId, documentId)), {
      method: 'GET',
      includeAuth: true,
      ...options
    });
  },

  deleteEmployeeDocument: async (userId, documentId, options = {}) => {
    return apiCall(getApiUrl(getCompanyUserDocumentDeleteEndpoint(userId, documentId)), {
      method: 'DELETE',
      includeAuth: true,
      ...options
    });
  },

  editEmployeeDocument: async (userId, documentId, documentData, options = {}) => {
    const url = getApiUrl(getCompanyUserDocumentDeleteEndpoint(userId, documentId));
    const payload = JSON.stringify(documentData);

    try {
      return await apiCall(url, {
        method: 'PATCH',
        includeAuth: true,
        body: payload,
        loadingMessage: 'Updating document...',
        ...options
      });
    } catch (error) {
      // Some deployments may not allow PATCH on this endpoint (405).
      if (String(error?.message || '').toLowerCase().includes('method not allowed')) {
        return await apiCall(url, {
          method: 'PUT',
          includeAuth: true,
          body: payload,
          loadingMessage: 'Updating document...',
          ...options
        });
      }
      throw error;
    }
  },

  // Get folder list
  getFolderList: async (options = {}) => {
    return apiCall(getApiUrl(COMPANY_API.GET_FOLDER_LIST), {
      method: 'GET',
      includeAuth: true,
      ...options
    });
  },

  // Get documents by folder
  getDocumentsByFolder: async (folderId, options = {}) => {
    return apiCall(getApiUrl(`${COMPANY_API.GET_ALL_DOCUMENTS}?folder_id=${folderId}`), {
      method: 'GET',
      includeAuth: true,
      ...options
    });
  },

  // Generate document download link
  downloadDocument: async (documentId, options = {}) => {
    return apiCall(getApiUrl(`${COMPANY_API.DOWNLOAD_DOCUMENT}/${documentId}`), {
      method: 'GET',
      includeAuth: true,
      ...options
    });
  },

  // Create folder
  createFolder: async (folderData, options = {}) => {
    return apiCall(getApiUrl(COMPANY_API.CREATE_FOLDER), {
      method: 'POST',
      includeAuth: true,
      body: JSON.stringify(folderData),
      ...options
    });
  },

  editFolder: async (folderId, folderData, options = {}) => {
    return apiCall(getApiUrl(`${COMPANY_API.EDIT_FOLDER}/${folderId}`), {
      method: 'PUT',
      includeAuth: true,
      body: JSON.stringify(folderData),
      ...options
    });
  },

  deleteFolder: async (folderId) => {
    return apiCall(getApiUrl(`${COMPANY_API.DELETE_FOLDER}/${folderId}`), {
      method: 'DELETE',
      includeAuth: true,
    });
  },

  // Edit document
  editDocument: async (documentId, documentData, options = {}) => {
    return apiCall(getApiUrl(`${COMPANY_API.EDIT_DOCUMENT}/${documentId}`), {
      method: 'PUT',
      includeAuth: true,
      body: documentData, // FormData for file upload
      ...options
    });
  },

  // Delete document
  deleteDocument: async (documentId) => {
    return apiCall(getApiUrl(`${COMPANY_API.DELETE_DOCUMENT}/${documentId}`), {
      method: 'DELETE',
      includeAuth: true,
    });
  },

  // Projects Management API calls
  getProjectsList: async (params = {}, options = {}) => {
    return apiCall(getApiUrl(COMPANY_API.GET_PROJECTS_LIST, params), {
      method: 'GET',
      includeAuth: true,
      ...options,
    });
  },

  createProject: async (projectData, options = {}) => {
    return apiCall(getApiUrl(COMPANY_API.CREATE_PROJECT), {
      method: 'POST',
      includeAuth: true,
      body: JSON.stringify(projectData),
      ...options
    });
  },

  editProject: async (projectId, projectData, options = {}) => {
    return apiCall(getApiUrl(`${COMPANY_API.EDIT_PROJECT}/${projectId}`), {
      method: 'PUT',
      includeAuth: true,
      body: JSON.stringify(projectData),
      ...options
    });
  },

  deleteProject: async (projectId) => {
    return apiCall(getApiUrl(`${COMPANY_API.DELETE_PROJECT}/${projectId}`), {
      method: 'DELETE',
      includeAuth: true,
    });
  },

  // Task Management API calls
  getTasksList: async (projectId) => {
    return apiCall(getApiUrl(`${COMPANY_API.TASKS_LIST}/${projectId}`), {
      method: 'GET',
      includeAuth: true,
    });
  },

  getTaskDetail: async (taskId) => {
    return apiCall(getApiUrl(`${COMPANY_API.TASK_DETAIL}/${taskId}`), {
      method: 'GET',
      includeAuth: true,
      showLoading: false,
    });
  },

  getTaskEnums: async (options = {}) => {
    return apiCall(getApiUrl(COMPANY_API.TASK_ENUMS), {
      method: 'GET',
      includeAuth: true,
      ...options
    });
  },

  editTask: async (taskData) => {
    return apiCall(getApiUrl(COMPANY_API.TASK_EDIT), {
      method: 'PUT',
      includeAuth: true,
      body: JSON.stringify(taskData),
    });
  },

  deleteTask: async (taskId, projectId) => {
    return apiCall(getApiUrl(`${COMPANY_API.TASK_DELETE}/${taskId}`), {
      method: 'DELETE',
      includeAuth: true,
    });
  },

  updateTaskStatus: async (taskId, status) => {
    return apiCall(getApiUrl(`${COMPANY_API.TASK_UPDATE_STATUS}/${taskId}`), {
      method: 'PATCH',
      includeAuth: true,
      body: JSON.stringify({ status }),
    });
  },

  startTask: async (taskId) => {
    return apiCall(getApiUrl(`${COMPANY_API.TASK_START}/${taskId}`), {
      method: 'POST',
      includeAuth: true,
      showLoading: false,
      body: JSON.stringify({}),
    });
  },

  endTask: async (taskId) => {
    return apiCall(getApiUrl(`${COMPANY_API.TASK_END}/${taskId}`), {
      method: 'POST',
      includeAuth: true,
      showLoading: false,
      body: JSON.stringify({}),
    });
  },

  getTaskTimelogs: async (taskId) => {
    return apiCall(getApiUrl(`${COMPANY_API.TASK_TIMELOGS}/${taskId}`), {
      method: 'GET',
      includeAuth: true,
      showLoading: false,
    });
  },

  // Update a specific task timelog (start/end time) by timelog ID
  updateTaskTimelog: async (timelogId, payload) => {
    return apiCall(getApiUrl(`${COMPANY_API.TASK_TIMELOGS_EDIT}/${timelogId}`), {
      method: 'PUT',
      includeAuth: true,
      showLoading: false,
      body: JSON.stringify(payload),
    });
  },

  // Create task comment
  postTaskComment: async (taskId, commentText) => {
    return apiCall(getApiUrl(`${COMPANY_API.TASK_COMMENT_CREATE}/${taskId}`), {
      method: 'POST',
      includeAuth: true,
      showLoading: false,
      body: JSON.stringify({ comment: commentText }),
    });
  },

  // Update task comment
  updateTaskComment: async (commentId, commentText) => {
    return apiCall(getApiUrl(`${COMPANY_API.TASK_COMMENT_UPDATE}/${commentId}`), {
      method: 'PUT',
      includeAuth: true,
      body: JSON.stringify({ comment: commentText }),
    });
  },

  // Delete task comment
  deleteTaskComment: async (commentId) => {
    return apiCall(getApiUrl(`${COMPANY_API.TASK_COMMENT_DELETE}/${commentId}`), {
      method: 'DELETE',
      includeAuth: true,
    });
  },

  // Get all company tasks
  getAllTasks: async (params = {}) => {
    return apiCall(getApiUrl(COMPANY_API.ALL_TASKS_LIST, params), {
      method: 'GET',
      includeAuth: true,
    });
  },

  // Public: Submit enquiry (no auth required)
  postEnquiry: async (payload) => {
    return apiCall(getApiUrl(COMPANY_API.ENQUIRIES), {
      method: 'POST',
      includeAuth: false,
      body: JSON.stringify(payload),
    });
  },

  // Public: Get policies (terms and privacy)
  getPolicies: async () => {
    return apiCall(getApiUrl(COMPANY_API.POLICIES), {
      method: 'GET',
      includeAuth: false,
    });
  },

  // Public: Get video tutorials for Learning Hub (no auth required)
  getVideoTutorials: async (options = {}) => {
    return apiCall(getApiUrl(COMPANY_API.VIDEO_TUTORIALS), {
      method: 'GET',
      includeAuth: false,
      ...options,
    });
  },

  // Digital Form templates
  getDigitalFormTemplates: async (options = {}) => {
    return apiCall(getApiUrl(COMPANY_API.DIGITAL_FORM_TEMPLATES), {
      method: 'GET',
      includeAuth: true,
      ...options,
    });
  },

  getDigitalFormClientForms: async (options = {}) => {
    return apiCall(getApiUrl(COMPANY_API.DIGITAL_FORM_CLIENT_FORMS), {
      method: 'GET',
      includeAuth: true,
      ...options,
    });
  },

  createDigitalFormClientForm: async (payload, options = {}) => {
    return apiCall(getApiUrl(COMPANY_API.DIGITAL_FORM_CLIENT_FORMS), {
      method: 'POST',
      includeAuth: true,
      body: JSON.stringify(payload),
      ...options,
    });
  },

  updateDigitalFormClientForm: async (clientFormId, payload, options = {}) => {
    return apiCall(getApiUrl(`${COMPANY_API.DIGITAL_FORM_CLIENT_FORMS}/${clientFormId}`), {
      method: 'PUT',
      includeAuth: true,
      body: JSON.stringify(payload),
      ...options,
    });
  },

  deleteDigitalFormClientForm: async (clientFormId, options = {}) => {
    return apiCall(getApiUrl(`${COMPANY_API.DIGITAL_FORM_CLIENT_FORMS}/${clientFormId}`), {
      method: 'DELETE',
      includeAuth: true,
      ...options,
    });
  },

  getMediaPresignedUrl: async (filePath, options = {}) => {
    return apiCall(getApiUrl(COMPANY_API.MEDIA_PRESIGNED_URL, { file_path: filePath }), {
      method: 'GET',
      includeAuth: true,
      showLoading: false,
      ...options,
    });
  },

  downloadMediaFile: async (filePath, options = {}) => {
    return apiCallBlob(getApiUrl(COMPANY_API.MEDIA_DOWNLOAD, { file_path: filePath }), {
      method: 'GET',
      includeAuth: true,
      showLoading: false,
      ...options,
    });
  },

  uploadDigitalFormAttachment: async (file, options = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiCall(getApiUrl(COMPANY_API.DIGITAL_FORM_ATTACHMENTS_UPLOAD), {
      method: 'POST',
      includeAuth: true,
      body: formData,
      loadingMessage: 'Uploading attachment...',
      ...options,
    });
  },

  submitDigitalFormClientForm: async (clientFormId, payload, options = {}) => {
    return apiCall(
      getApiUrl(`${COMPANY_API.DIGITAL_FORM_CLIENT_FORMS}/${clientFormId}/submissions`),
      {
        method: 'POST',
        includeAuth: true,
        body: JSON.stringify(payload),
        ...options,
      },
    );
  },

  getDigitalFormClientFormSubmissions: async (clientFormId, options = {}) => {
    return apiCall(
      getApiUrl(`${COMPANY_API.DIGITAL_FORM_CLIENT_FORMS}/${clientFormId}/submissions`),
      {
        method: 'GET',
        includeAuth: true,
        ...options,
      },
    );
  },

  getDigitalFormSubmissionsByDestination: async (params = {}, options = {}) => {
    return apiCall(getApiUrl(COMPANY_API.DIGITAL_FORM_SUBMISSIONS_BY_DESTINATION, params), {
      method: 'GET',
      includeAuth: true,
      ...options,
    });
  },

  // Change Password
  changePassword: async ({ old_password, new_password }, options = {}) => {
    return apiCall(getApiUrl(COMPANY_API.CHANGE_PASSWORD), {
      method: 'PUT',
      includeAuth: true,
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: `old_password=${encodeURIComponent(old_password)}&new_password=${encodeURIComponent(new_password)}`,
      loadingMessage: 'Changing password...',
      ...options
    });
  },

  // Notification Preferences
  getNotificationPreferences: async (options = {}) => {
    return apiCall(getApiUrl(COMPANY_API.NOTIFICATION_PREFERENCES), {
      method: 'GET',
      includeAuth: true,
      ...options
    });
  },

  updateNotificationPreferences: async (preferences, options = {}) => {
    return apiCall(getApiUrl(COMPANY_API.NOTIFICATION_PREFERENCES), {
      method: 'PUT',
      includeAuth: true,
      body: JSON.stringify(preferences),
      loadingMessage: 'Updating notification preferences...',
      ...options
    });
  },

  // Employee Management API calls
  getAllEmployees: async (options = {}) => {
    return apiCall(getApiUrl(COMPANY_API.GET_ALL_EMPLOYEES), {
      method: 'GET',
      includeAuth: true,
      ...options
    });
  },

  createEmployee: async (employeeData, options = {}) => {
    const isFormData = employeeData instanceof FormData;
    const body = isFormData ? employeeData : JSON.stringify(employeeData);
    return apiCall(getApiUrl(COMPANY_API.CREATE_EMPLOYEE), {
      method: 'POST',
      includeAuth: true,
      body,
      ...options
    });
  },

  createAssetRecord: async (assetData, options = {}) => {
    return apiCall(getApiUrl(COMPANY_API.CREATE_ASSET_RECORD), {
      method: 'POST',
      includeAuth: true,
      body: JSON.stringify(assetData),
      loadingMessage: 'Creating asset record...',
      ...options,
    });
  },

  getAssetRecords: async (options = {}) => {
    return apiCall(getApiUrl(COMPANY_API.GET_ASSET_RECORDS), {
      method: 'GET',
      includeAuth: true,
      loadingMessage: 'Loading asset records...',
      ...options,
    });
  },

  getSitesList: async (options = {}) => {
    return apiCall(getApiUrl(COMPANY_API.GET_SITES_LIST), {
      method: 'GET',
      includeAuth: true,
      loadingMessage: 'Loading sites...',
      ...options,
    });
  },

  createSite: async (siteData, options = {}) => {
    return apiCall(getApiUrl(COMPANY_API.CREATE_SITE), {
      method: 'POST',
      includeAuth: true,
      body: JSON.stringify(siteData),
      loadingMessage: 'Creating site...',
      ...options,
    });
  },

  updateSite: async (siteId, siteData, options = {}) => {
    return apiCall(getApiUrl(`${COMPANY_API.EDIT_SITE}/${siteId}`), {
      method: 'PUT',
      includeAuth: true,
      body: JSON.stringify(siteData),
      loadingMessage: 'Updating site...',
      ...options,
    });
  },

  deleteSite: async (siteId, options = {}) => {
    return apiCall(getApiUrl(`${COMPANY_API.DELETE_SITE}/${siteId}`), {
      method: 'DELETE',
      includeAuth: true,
      loadingMessage: 'Deleting site...',
      ...options,
    });
  },

  getSiteDetail: async (siteId, options = {}) => {
    return apiCall(getApiUrl(getSiteDetailEndpoint(siteId)), {
      method: 'GET',
      includeAuth: true,
      loadingMessage: 'Loading site details...',
      ...options,
    });
  },

  getSiteDocuments: async (siteId, options = {}) => {
    return apiCall(getApiUrl(getSiteDocumentsEndpoint(siteId)), {
      method: 'GET',
      includeAuth: true,
      loadingMessage: 'Loading site documents...',
      ...options,
    });
  },

  uploadSiteDocument: async (siteId, documentData, options = {}) => {
    return apiCall(getApiUrl(getSiteDocumentsEndpoint(siteId)), {
      method: 'POST',
      includeAuth: true,
      body: documentData,
      loadingMessage: 'Uploading document...',
      ...options,
    });
  },

  downloadSiteDocument: async (siteId, documentId, options = {}) => {
    return apiCall(getApiUrl(getSiteDocumentDownloadEndpoint(siteId, documentId)), {
      method: 'GET',
      includeAuth: true,
      loadingMessage: 'Preparing download...',
      ...options,
    });
  },

  deleteSiteDocument: async (siteId, documentId, options = {}) => {
    return apiCall(getApiUrl(getSiteDocumentDeleteEndpoint(siteId, documentId)), {
      method: 'DELETE',
      includeAuth: true,
      loadingMessage: 'Deleting document...',
      ...options,
    });
  },

  updateAssetRecord: async (assetId, assetData, options = {}) => {
    return apiCall(getApiUrl(`${COMPANY_API.EDIT_ASSET_RECORD}/${assetId}`), {
      method: 'PUT',
      includeAuth: true,
      body: JSON.stringify(assetData),
      loadingMessage: 'Updating asset record...',
      ...options,
    });
  },

  deleteAssetRecord: async (assetId, options = {}) => {
    return apiCall(getApiUrl(`${COMPANY_API.DELETE_ASSET_RECORD}/${assetId}`), {
      method: 'DELETE',
      includeAuth: true,
      loadingMessage: 'Deleting asset record...',
      ...options,
    });
  },

  updateEmployee: async (employeeId, employeeData, options = {}) => {
    const isFormData = employeeData instanceof FormData;
    const body = isFormData ? employeeData : JSON.stringify(employeeData);
    return apiCall(getApiUrl(`${COMPANY_API.UPDATE_EMPLOYEE}/${employeeId}`), {
      method: 'PUT',
      includeAuth: true,
      body,
      ...options
    });
  },

  deleteEmployee: async (employeeId) => {
    return apiCall(getApiUrl(`${COMPANY_API.DELETE_EMPLOYEE}/${employeeId}`), {
      method: 'DELETE',
      includeAuth: true,
    });
  },

  // Role Management API calls
  getAllRoles: async (options = {}) => {
    return apiCall(getApiUrl(COMPANY_API.GET_ALL_ROLES), {
      method: 'GET',
      includeAuth: true,
      ...options
    });
  },

  createRole: async (roleData) => {
    return apiCall(getApiUrl(COMPANY_API.CREATE_ROLE), {
      method: 'POST',
      includeAuth: true,
      body: JSON.stringify(roleData),
    });
  },

  updateRole: async (roleId, roleData) => {
    return apiCall(getApiUrl(`${COMPANY_API.UPDATE_ROLE}/${roleId}`), {
      method: 'PUT',
      includeAuth: true,
      body: JSON.stringify(roleData),
    });
  },

  editRole: async (roleId, roleData) => {
    const url = getApiUrl(`${COMPANY_API.EDIT_ROLE}/${roleId}`);
    console.warn('[api_service.editRole] Calling API', { url, roleId, body: roleData });
    return apiCall(url, {
      method: 'PUT',
      includeAuth: true,
      body: JSON.stringify(roleData),
    });
  },

  deleteRole: async (roleId) => {
    return apiCall(getApiUrl(`${COMPANY_API.DELETE_ROLE}/${roleId}`), {
      method: 'DELETE',
      includeAuth: true,
    });
  },

  getModulesWithPermissions: async (roleId) => {
    return apiCall(getApiUrl(`${COMPANY_API.GET_MODULES_WITH_PERMISSIONS}/${roleId}`), {
      method: 'GET',
      includeAuth: true,
    });
  },

  // Modules API calls
  getModules: async (options = {}) => {
    return apiCall(getApiUrl(COMPANY_API.GET_MODULES), {
      method: 'GET',
      includeAuth: true,
      ...options
    });
  },

  // Subscription Plans API calls
  getSubscriptionPlans: async (includeTrial = false) => {
    const url = new URL(getApiUrl(COMPANY_API.GET_SUBSCRIPTION_PLAN));
    url.searchParams.append('include_trial', includeTrial.toString());
    
    return apiCall(url.toString(), {
      method: 'GET',
    });
  },

  // Get My Subscription
  getMySubscription: async () => {
    return apiCall(getApiUrl(COMPANY_API.GET_MY_SUBSCRIPTION), {
      method: 'GET',
      includeAuth: true,
    });
  },

  // Create Subscription
  createSubscription: async (subscriptionData) => {
    return apiCall(getApiUrl(COMPANY_API.CREATE_SUBSCRIPTION), {
      method: 'POST',
      body: JSON.stringify(subscriptionData),
      includeAuth: true,
      loadingMessage: 'Creating subscription...'
    });
  },

  // Dashboard APIs
  getDashboard: async (options = {}) => {
    return apiCall(getApiUrl(COMPANY_API.GET_DASHBOARD), {
      method: 'GET',
      includeAuth: true,
      ...options
    });
  },

  getAssignedProjects: async () => {
    return apiCall(getApiUrl(COMPANY_API.GET_ASSIGNED_PROJECTS), {
      method: 'GET',
      includeAuth: true,
    });
  },

  getTodaysTasks: async () => {
    return apiCall(getApiUrl(COMPANY_API.GET_TODAYS_TASKS), {
      method: 'GET',
      includeAuth: true,
    });
  },

  getNotifications: async (options = {}) => {
    return apiCall(getApiUrl(COMPANY_API.GET_NOTIFICATIONS), {
      method: 'GET',
      includeAuth: true,
      ...options
    });
  },

  markNotificationRead: async (notificationId, options = {}) => {
    const url = getApiUrl(COMPANY_API.MARK_NOTIFICATION_READ).replace(':id', notificationId);
    return apiCall(url, {
      method: 'PATCH',
      includeAuth: true,
      ...options
    });
  },

  markAllNotificationsRead: async (options = {}) => {
    return apiCall(getApiUrl(COMPANY_API.MARK_ALL_NOTIFICATIONS_READ), {
      method: 'PATCH',
      includeAuth: true,
      ...options
    });
  },
};

// Stripe Payment API calls
export const stripeAPI = {
  // Create Stripe checkout session
  createCheckoutSession: async (checkoutData) => {
    return apiCall(getApiUrl(COMPANY_API.CREATE_STRIPE_CHECKOUT_SESSION), {
      method: 'POST',
      includeAuth: true,
      body: JSON.stringify(checkoutData),
    });
  },

  // Store payment success data
  paymentSuccess: async (paymentData) => {
    return apiCall(getApiUrl(COMPANY_API.STRIPE_PAYMENT_SUCCESS), {
      method: 'POST',
      includeAuth: true,
      body: JSON.stringify(paymentData),
    });
  },

  // Retrieve complete Stripe session data
  retrieveSession: async (sessionId) => {
    return apiCall(getApiUrl(`${COMPANY_API.RETRIEVE_STRIPE_SESSION}/${sessionId}`), {
      method: 'GET',
      includeAuth: true,
    });
  },

  // Cancel subscription
  cancelSubscription: async (subscriptionId, options = {}) => {
    return apiCall(getApiUrl(COMPANY_API.CANCEL_SUBSCRIPTION), {
      method: 'POST',
      includeAuth: true,
      body: JSON.stringify({ subscription_id: subscriptionId }),
      loadingMessage: 'Canceling subscription...',
      ...options
    });
  },
};

// Export the apiCall function for use in other modules
export { apiCall };

// Utility function to get device info
export const getDeviceInfo = () => {
  const userAgent = navigator.userAgent;
  let deviceType = 'desktop';
  let deviceName = '';
  let osName = '';
  let osVersion = '';
  let browserName = '';
  let browserVersion = '';

  // Detect device type
  if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
    deviceType = 'mobile';
  } else if (/Tablet|iPad/i.test(userAgent)) {
    deviceType = 'tablet';
  }

  // Detect OS
  if (userAgent.includes('Windows')) {
    osName = 'Windows';
    const match = userAgent.match(/Windows NT (\d+\.\d+)/);
    if (match) osVersion = match[1];
  } else if (userAgent.includes('Mac OS X')) {
    osName = 'macOS';
    const match = userAgent.match(/Mac OS X (\d+[._]\d+)/);
    if (match) osVersion = match[1].replace('_', '.');
  } else if (userAgent.includes('Linux')) {
    osName = 'Linux';
  } else if (userAgent.includes('Android')) {
    osName = 'Android';
    const match = userAgent.match(/Android (\d+\.\d+)/);
    if (match) osVersion = match[1];
  } else if (userAgent.includes('iOS')) {
    osName = 'iOS';
    const match = userAgent.match(/OS (\d+[._]\d+)/);
    if (match) osVersion = match[1].replace('_', '.');
  }

  // Detect browser
  if (userAgent.includes('Chrome')) {
    browserName = 'Chrome';
    const match = userAgent.match(/Chrome\/(\d+\.\d+)/);
    if (match) browserVersion = match[1];
  } else if (userAgent.includes('Firefox')) {
    browserName = 'Firefox';
    const match = userAgent.match(/Firefox\/(\d+\.\d+)/);
    if (match) browserVersion = match[1];
  } else if (userAgent.includes('Safari')) {
    browserName = 'Safari';
    const match = userAgent.match(/Version\/(\d+\.\d+)/);
    if (match) browserVersion = match[1];
  } else if (userAgent.includes('Edge')) {
    browserName = 'Edge';
    const match = userAgent.match(/Edge\/(\d+\.\d+)/);
    if (match) browserVersion = match[1];
  }

  // Create device info object with only required fields and skip unknown values
  const deviceInfo = {};
  
  if (deviceType) deviceInfo.device_type = deviceType;
  if (deviceName) deviceInfo.device_name = deviceName;
  if (osName) deviceInfo.os_name = osName;
  if (osVersion) deviceInfo.os_version = osVersion;
  if (browserName) deviceInfo.browser_name = browserName;
  if (browserVersion) deviceInfo.browser_version = browserVersion;
  
  // Return as JSON string
  return JSON.stringify(deviceInfo);
}; 